import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useActions, { useAppSelector } from "../../app/hooks";
import { selectUser } from "../auth/authSlice";
import { apiGetFileData } from "../../services/RepoAPI";

const FORMAT_FILE_NAME = "file_name_format.json";
const REFERENCE_FILE_NAME = "reference1.txt";

const CURRENT_DATETIME_PLACEHOLDER = "[CURRENT_DATETIME_CONDENSED]";

function getUtcDateTimeCondensed(): string {
  const d = new Date();
  const yy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${yy}${mm}${dd}${hh}${min}${ss}`;
}

function suggestedNameFromFormatJson(obj: Record<string, unknown>): string {
  const keys = Object.keys(obj).filter((k) => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
  const values = keys.map((k) => {
    const v = String(obj[k] ?? "");
    return v === CURRENT_DATETIME_PLACEHOLDER ? getUtcDateTimeCondensed() : v;
  }).filter(Boolean);
  return values.join("") || "";
}

export default function CreateFileForm(props: {
  open: boolean;
  repoId: number;
  repoPath: string;
  parentCloseHandle: () => void;
}) {
  const actions = useActions();
  const user = useAppSelector(selectUser);

  const [name, setName] = useState("");
  const [nameError, setNameError] = React.useState("Name");
  const [referenceAvailable, setReferenceAvailable] = useState(false);
  const [referenceContent, setReferenceContent] = useState<string | null>(null);
  const [useReference, setUseReference] = useState(false);

  useEffect(() => {
    if (!props.open) {
      setName("");
      setReferenceAvailable(false);
      setReferenceContent(null);
      setUseReference(false);
      return;
    }
    if (!user) return;
    const repoFieldName = props.repoPath === "/" ? FORMAT_FILE_NAME : `${props.repoPath}/${FORMAT_FILE_NAME}`;
    const refFieldName = props.repoPath === "/" ? REFERENCE_FILE_NAME : `${props.repoPath}/${REFERENCE_FILE_NAME}`;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGetFileData(
          user.default_organization_id,
          user.default_workspace_id,
          props.repoId,
          repoFieldName,
        );
        if (cancelled) return;
        const currentCode = res.data?.current_code;
        if (typeof currentCode !== "string") return;
        const parsed = JSON.parse(currentCode) as Record<string, unknown>;
        const suggested = suggestedNameFromFormatJson(parsed);
        if (suggested) setName(suggested);
      } catch {
        // Leave name as is on error (e.g. format file not present)
      }
    })();
    return () => { cancelled = true; };
  }, [props.open, props.repoId, props.repoPath, user]);

  useEffect(() => {
    if (!props.open || !user) return;
    const refFieldName = props.repoPath === "/" ? REFERENCE_FILE_NAME : `${props.repoPath}/${REFERENCE_FILE_NAME}`;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGetFileData(
          user.default_organization_id,
          user.default_workspace_id,
          props.repoId,
          refFieldName,
        );
        if (cancelled) return;
        const currentCode = res.data?.current_code;
        setReferenceAvailable(typeof currentCode === "string");
        setReferenceContent(typeof currentCode === "string" ? currentCode : null);
      } catch {
        setReferenceAvailable(false);
        setReferenceContent(null);
      }
    })();
    return () => { cancelled = true; };
  }, [props.open, props.repoId, props.repoPath, user]);

  function createRepoFile() {
    const cfProps = {
      file_name: props.repoPath + "/" + name,
      repoId: props.repoId,
      file_path: props.repoPath,
      ...(useReference && referenceContent != null ? { initialCode: referenceContent } : {}),
    };

    actions.createRepoFile(cfProps);
    props.parentCloseHandle();
  }

  function createPreambleRepoFile() {
    const cfProps = {
      file_name: props.repoPath + "/prompt_preamble.txt",
      repoId: props.repoId,
      file_path: props.repoPath,
    };

    console.log(cfProps);

    actions.createRepoFile(cfProps);
    props.parentCloseHandle();
  }

  function createFileNameFormatRepoFile() {
    const cfProps = {
      file_name: props.repoPath + "/file_name_format.json",
      repoId: props.repoId,
      file_path: props.repoPath,
    };

    console.log(cfProps);

    actions.createRepoFile(cfProps);
    props.parentCloseHandle();
  }

  function handleNameChange(e: any) {
    if (e.target.value.trim() === "") {
      setNameError("Invalid name");
    } else setNameError("");
    setName(e.target.value);
  }

  return (
    <Dialog open={props.open} onClose={() => props.parentCloseHandle()}>
      <DialogTitle>New File</DialogTitle>
      <DialogContent>
        <DialogContentText>Create a new file</DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="File name"
          error={nameError !== ""}
          fullWidth
          variant="standard"
          value={name}
          onChange={handleNameChange}
        />
        {referenceAvailable && (
          <FormControlLabel
            control={
              <Checkbox
                checked={useReference}
                onChange={(e) => setUseReference(e.target.checked)}
                name="useReference"
              />
            }
            label="USE REFERENCE"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.parentCloseHandle()}>Cancel</Button>
        <Button
          onClick={createRepoFile}
          type="submit"
          variant="contained"
          color="primary"
          disabled={nameError !== ""}
        >
          Create
        </Button>
      </DialogActions>
      <DialogActions>
        <Button
          onClick={createPreambleRepoFile}
          type="submit"
          variant="contained"
          color="primary"
        >
          Create 'prompt_preamble.txt' File
        </Button>
      </DialogActions>
      <DialogActions>
        <Button
          onClick={createFileNameFormatRepoFile}
          type="submit"
          variant="contained"
          color="primary"
        >
          Create 'file_name_format.json' File
        </Button>
      </DialogActions>
    </Dialog>
  );
}
